import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OrgCredential } from '../../interfaces/organization';
import { ToolConfig } from '../../interfaces/toolConfig';
import {
  fetchToolConfigs,
  createToolConfig,
  updateToolConfig,
  updateCredential,
} from '../ActionTypes/organizationActions';

export interface OrganizationState {
  org: OrgCredential | null;
  loading: boolean;
  credentials: OrgCredential[];
  toolConfigs: ToolConfig[];
  isLoadingToolConfigs: boolean;
  isSavingToolConfig: boolean;
  loadingByCredentialId: Record<string, boolean>;
  deletingByCredentialId: Record<string, boolean>;
}

const initialState: OrganizationState = {
  org: null,
  loading: true,
  credentials: [],
  toolConfigs: [],
  isLoadingToolConfigs: false,
  isSavingToolConfig: false,
  loadingByCredentialId: {},
  deletingByCredentialId: {},
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    resetOrganization: () => {
      return initialState;
    },
    setOrganization: (state, action: PayloadAction<OrgCredential | null>) => {
      state.org = action.payload;
      state.loading = false;
    },
    setAllCredentials: (state, action: PayloadAction<OrgCredential[]>) => {
      state.credentials = action.payload;
    },
    setDeletingCredetial: (
      state,
      action: PayloadAction<Record<string, boolean>>
    ) => {
      state.deletingByCredentialId = {
        ...state.deletingByCredentialId,
        ...action.payload,
      };
    },
    clearDeletingCredetial: (state, action: PayloadAction<string>) => {
      delete state.deletingByCredentialId[action.payload];
    },
    setToolConfigs: (state, action: PayloadAction<ToolConfig[]>) => {
      state.toolConfigs = action.payload;
    },
    setLoadingToolConfigs: (state, action: PayloadAction<boolean>) => {
      state.isLoadingToolConfigs = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCredential.pending, (state, action) => {
        const credentialId = action.meta.arg.id;
        state.loadingByCredentialId[credentialId] = true;
      })
      .addCase(updateCredential.fulfilled, (state, action) => {
        const updatedCredential = action.payload.data;
        const credentialId = updatedCredential.id;
        // Update the credential in the credentials array
        state.credentials = state.credentials.map((cred) =>
          cred.id === updatedCredential.id ? updatedCredential : cred
        );
        // If this is the currently selected org, update it as well
        if (state.org?.id === updatedCredential.id) {
          state.org = updatedCredential;
        }
        // Clear loading state
        state.loadingByCredentialId[credentialId] = false;
      })
      .addCase(updateCredential.rejected, (state, action) => {
        const credentialId = action.meta.arg.id;
        state.loadingByCredentialId[credentialId] = false;
      })

      .addCase(fetchToolConfigs.pending, (state) => {
        state.isLoadingToolConfigs = true;
      })
      .addCase(fetchToolConfigs.fulfilled, (state, action) => {
        state.isLoadingToolConfigs = false;
        state.toolConfigs = action.payload.data;
      })
      .addCase(fetchToolConfigs.rejected, (state) => {
        state.isLoadingToolConfigs = false;
      })

      .addCase(createToolConfig.pending, (state) => {
        state.isSavingToolConfig = true;
      })
      .addCase(createToolConfig.fulfilled, (state, action) => {
        state.isSavingToolConfig = false;
        // Add the new tool config to the existing list
        state.toolConfigs.push(action.payload.data);
      })
      .addCase(createToolConfig.rejected, (state) => {
        state.isSavingToolConfig = false;
      })

      .addCase(updateToolConfig.pending, (state) => {
        state.isSavingToolConfig = true;
      })
      .addCase(updateToolConfig.fulfilled, (state, action) => {
        state.isSavingToolConfig = false;
        // Update the existing tool config
        state.toolConfigs = state.toolConfigs.map((toolConfig) =>
          toolConfig.id === action.payload.data.id
            ? action.payload.data
            : toolConfig
        );
      })
      .addCase(updateToolConfig.rejected, (state) => {
        state.isSavingToolConfig = false;
      });
  },
});

export const {
  resetOrganization,
  setOrganization,
  setAllCredentials,
  setDeletingCredetial,
  setToolConfigs,
  setLoadingToolConfigs,
} = organizationSlice.actions;

export default organizationSlice.reducer;
